import { useMemo, useState } from "react";
import { causeNote, changedSinceClosing, editsMasterData, inventoryReport, reasonLabel, uuidv7, wineReasons } from "@lagerlog/core";
import type { Booking, Inventory, InventoryReport, InventoryRow } from "@lagerlog/core";
import { Containers, containerWords, euro, inUnits, otherContainers } from "../catalog.ts";
import type { Catalog } from "../catalog.ts";
import { useCatalog, useDrinks, useLedger, useStore } from "../hooks.ts";
import { useWide } from "../wide.ts";
import { Named, fmt, fmtDiff } from "./Book.tsx";
import { Lines, Parts } from "./Stock.tsx";
import { download, euroCell, toCsv } from "../download.ts";

const date = (at: number) => new Date(at).toLocaleDateString("de-DE");
const dateTime = (at: number) => new Date(at).toLocaleString("de-DE", { day: "2-digit", month: "2-digit", year: "numeric", hour: "2-digit", minute: "2-digit" });

/**
 * Inventory as a process (I1, I3, I4; docs/03-use-cases.md): create it, count with several people
 * under Zählen, assign known causes to differences, close it. The report is computed anew each
 * time from the journal; on closing it is frozen, and a late booking shows as a change.
 */
export function InventoryView() {
  const { store, version } = useStore();
  const catalog = useCatalog();
  const ledger = useLedger();
  const wide = useWide();
  const [shown, setShown] = useState<string | null>(null);
  const [creating, setCreating] = useState(false);

  const inventories = useMemo(() => [...(catalog?.inventories.values() ?? [])].sort((a, b) => b.startedAt - a.startedAt), [catalog]);
  const open = inventories.find((i) => i.status === "open");
  // Without a choice: the running inventory, else the latest closed one.
  const current = inventories.find((i) => i.id === shown) ?? open ?? inventories[0] ?? null;

  const report = useMemo(() => (ledger && store.replica && current ? inventoryReport(ledger, store.replica.journal(), current, Date.now()) : null), [ledger, current, version]); // eslint-disable-line react-hooks/exhaustive-deps

  if (!catalog || !ledger) return null;
  // Staff count blind (B2): the report with the stock in the books is for owner and management.
  if (!editsMasterData(store.rights().role))
    return (
      <div className="inventory">
        <p>
          <a href="#/zaehlen">← Zählen</a>
        </p>
        <p className="muted" data-testid="inventory-staff">
          Inventuren führen Inhaber und Verwaltung. Gezählt wird unter Zählen; den Bestand laut Buch und die Differenzen sehen nur sie.
        </p>
      </div>
    );

  return (
    <div className="inventory">
      <p className="row between">
        <a href="#/zaehlen">← Zählen</a>
        {current && current.id !== open?.id && open && (
          <button className="link" onClick={() => setShown(null)}>
            Zur laufenden Inventur
          </button>
        )}
      </p>
      {!open &&
        (creating || inventories.length === 0 ? (
          <NewInventory catalog={catalog} onDone={() => [setCreating(false), setShown(null)]} />
        ) : (
          <button className="primary" onClick={() => setCreating(true)} data-testid="inventory-new-open">
            Neue Inventur anlegen
          </button>
        ))}
      {current && report && <Report inventory={current} report={report} catalog={catalog} wide={wide} />}
      {inventories.some((i) => i.status === "closed") && (
        <section>
          <h3>Frühere Inventuren</h3>
          <ul className="list">
            {inventories
              .filter((i) => i.status === "closed")
              .map((i) => (
                <li key={i.id} className={current?.id === i.id ? "selected" : ""}>
                  <button className="row between" onClick={() => setShown(i.id)}>
                    <span>
                      {i.name}
                      <br />
                      <span className="muted">
                        {date(i.startedAt)} bis {i.closedAt ? date(i.closedAt) : "–"} · {i.lagers.map((l) => catalog.lagers.get(l)?.name ?? l).join(", ")}
                      </span>
                    </span>
                    <span className="side">
                      <span>{i.report ? `Schwund ${i.report.rows.filter((r) => r.rest < 0).reduce((c, r) => c.add(catalog.articles.get(r.article), r.rest), new Containers()).text()}` : ""}</span>
                      {store.prices && <span className="muted">{i.report ? euro(i.report.rows.filter((r) => r.rest < 0).reduce((s, r) => s + r.restValue, 0)) : ""}</span>}
                    </span>
                  </button>
                </li>
              ))}
          </ul>
        </section>
      )}
    </div>
  );
}

function NewInventory({ catalog, onDone }: { catalog: Catalog; onDone: () => void }) {
  const { store } = useStore();
  const lagers = [...catalog.lagers.values()].filter((l) => store.config?.lagers.includes(l.id));
  const month = new Date().toLocaleDateString("de-DE", { month: "long", year: "numeric" });
  const [name, setName] = useState(`Inventur ${month}`);
  const [chosen, setChosen] = useState<Set<string>>(() => new Set(lagers.map((l) => l.id)));
  const [blind, setBlind] = useState(true);

  async function create() {
    await store.edit("inventory", uuidv7(Date.now()), { name: name.trim() || `Inventur ${month}`, lagers: [...chosen], blind, startedAt: Date.now(), status: "open" });
    onDone();
  }

  return (
    <div className="card">
      <h2>Inventur anlegen</h2>
      <p className="muted">
        Alle zählen unter „Zählen“, auf beliebig vielen Geräten, auch ohne Netz. Was niemand zählt, bleibt „nicht gezählt“ und wird nicht zur Fehlmenge. Die Differenzen ordnest du hier zu; dann schließt du ab und der Bericht wird
        eingefroren.
      </p>
      <label>
        Name
        <input value={name} onChange={(e) => setName(e.target.value)} maxLength={60} data-testid="inventory-name" />
      </label>
      <p className="muted">Welche Lager?</p>
      {lagers.map((l) => (
        <label key={l.id} className="check">
          <input
            type="checkbox"
            checked={chosen.has(l.id)}
            onChange={(e) => {
              const next = new Set(chosen);
              if (e.target.checked) next.add(l.id);
              else next.delete(l.id);
              setChosen(next);
            }}
          />
          {l.name}
        </label>
      ))}
      <label className="check">
        <input type="checkbox" checked={blind} onChange={(e) => setBlind(e.target.checked)} />
        blind zählen (wer zählt, sieht den Bestand laut Buch nicht)
      </label>
      <button className="primary big" disabled={chosen.size === 0} onClick={create} data-testid="inventory-create">
        Inventur anlegen
      </button>
    </div>
  );
}

function Report({ inventory, report, catalog, wide }: { inventory: Inventory; report: InventoryReport; catalog: Catalog; wide: boolean }) {
  const drinks = useDrinks();
  const { store } = useStore();
  const [assigning, setAssigning] = useState<string | null>(null);
  const [closing, setClosing] = useState(false);
  const closed = inventory.status === "closed";
  const shownReport = closed && inventory.report ? inventory.report : report;
  const changes = closed && inventory.report ? changedSinceClosing(inventory.report, report) : [];
  const place = (id: string) => {
    const l = catalog.locations.get(id);
    return l ? `${catalog.lagers.get(l.lager)?.name ?? l.lager} · ${l.name}` : id;
  };
  const name = (id: string) => catalog.articles.get(id)?.name ?? id;
  /** A quantity of a wine as shown („2 Fl.“), signed for a difference; in bottles for a sum and the table. */
  const fl = (article: string, qty: number) => fmt(catalog.articles.get(article), qty);
  const signedFl = (article: string, qty: number) => fmtDiff(catalog.articles.get(article), qty);
  const units = (article: string, qty: number) => inUnits(catalog.articles.get(article), qty);

  // Progress per location: counted wines of all wines there.
  const locations = [...catalog.locations.values()].filter((l) => inventory.lagers.includes(l.lager) && store.config?.lagers.includes(l.lager));
  const progress = locations.map((l) => {
    const counted = shownReport.rows.filter((r) => r.location === l.id).length;
    const open = shownReport.uncounted.filter((u) => u.location === l.id).length;
    return { location: l, counted, total: counted + open };
  });

  async function close() {
    await store.edit("inventory", inventory.id, { status: "closed", closedAt: Date.now(), report });
    setClosing(false);
  }

  // Staff and viewers see quantities only (F20).
  const prices = store.prices;

  function exportCsv() {
    // Kegs and cans among the bottles: a column says the container, the headings no longer „Fl.“.
    const mixed = otherContainers([...shownReport.rows, ...shownReport.uncounted].map((r) => catalog.articles.get(r.article)));
    const unit = mixed ? "" : " (Fl.)";
    const kind = (article: string) => (mixed ? [catalog.articles.get(article) ? containerWords(catalog.articles.get(article)!).many : ""] : []);
    const rows: unknown[][] = [[drinks ? "Getränk" : "Wein", "Lagerort", "Soll laut Journal", "Gezählt", `Differenz${unit}`, "Differenz (€)", "Zugeordnet", `Schwund/Mehr${unit}`, "Schwund/Mehr (€)", ...(mixed ? ["Gebinde"] : [])]];
    for (const r of shownReport.rows) {
      const u = (q: number) => units(r.article, q);
      rows.push([name(r.article), place(r.location), u(r.expected), u(r.counted), u(r.diff), euroCell(r.value), r.causes.map((c) => `${u(c.qty)} ${reasonLabel(c.reason)}`).join(", "), u(r.rest), euroCell(r.restValue), ...kind(r.article)]);
    }
    for (const x of shownReport.uncounted) rows.push([name(x.article), place(x.location), units(x.article, x.qty), "nicht gezählt", "", "", "", "", "", ...kind(x.article)]);
    const shown = prices ? rows : rows.map((row) => row.filter((_, i) => i !== 5 && i !== 8));
    download(`${inventory.name}.csv`, toCsv(shown), "text/csv;charset=utf-8");
  }

  const t = shownReport.totals;
  // Bottles of different sizes add up as bottles, not as ml; kegs and cans apart from bottles.
  const sum = (f: (r: InventoryRow) => number) => shownReport.rows.reduce((c, r) => c.add(catalog.articles.get(r.article), f(r)), new Containers());
  const diffBottles = sum((r) => r.diff);
  const causeBottles = sum((r) => -r.causes.reduce((s, c) => s + c.qty, 0));
  // Shrinkage and more than booked apart: netted, a few found bottles would hide the missing ones.
  const part = (f: (r: InventoryRow) => boolean) => shownReport.rows.filter(f).reduce((a, r) => ({ qty: a.qty.add(catalog.articles.get(r.article), r.rest), value: a.value + r.restValue }), { qty: new Containers(), value: 0 });
  const short = part((r) => r.rest < 0);
  const more = part((r) => r.rest > 0);
  return (
    <div className={wide ? "split wide-left" : ""}>
      <div className="pane">
        <h2>{inventory.name}</h2>
        <p className="muted">
          {inventory.lagers.map((l) => catalog.lagers.get(l)?.name ?? l).join(", ")} · {inventory.blind ? "blind" : "offen"} · seit {dateTime(inventory.startedAt)}
          {closed && inventory.closedAt ? ` · abgeschlossen ${dateTime(inventory.closedAt)}` : ""}
        </p>
        {changes.length > 0 && (
          <div className="banner-box" data-testid="inventory-changes">
            <strong>Seit dem Abschluss geändert</strong> (verspätete Buchungen):
            <ul>
              {changes.map((c) => (
                <li key={`${c.article}|${c.location}`}>
                  {name(c.article)}, {place(c.location)}: {c.before < 0 || c.after < 0 ? "Schwund" : "Differenz"} von {fl(c.article, Math.abs(c.before))} auf {fl(c.article, Math.abs(c.after))}
                </li>
              ))}
            </ul>
          </div>
        )}
        {!closed && (
          <table className="grid">
            <thead>
              <tr>
                <th>Lagerort</th>
                <th className="num">gezählt</th>
                <th />
              </tr>
            </thead>
            <tbody>
              {progress.map((p) => (
                <tr key={p.location.id}>
                  <td>{place(p.location.id)}</td>
                  <td className="num">
                    {p.counted} von {p.total}
                  </td>
                  <td className="num">
                    <a href={`#/zaehlen?ort=${encodeURIComponent(p.location.id)}`}>zählen</a>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}

        <h3>Differenzen</h3>
        {shownReport.rows.filter((r) => r.diff !== 0).length === 0 && <p className="muted">Bisher stimmt alles Gezählte mit dem Journal überein.</p>}
        <ul className="list">
          {shownReport.rows
            .filter((r) => r.diff !== 0 || r.causes.length > 0)
            .map((r) => {
              const key = `${r.article}|${r.location}`;
              return (
                <li key={key} className="entry-block">
                  <div className="row between">
                    <span>
                      {catalog.articles.get(r.article) ? <Named a={catalog.articles.get(r.article)!} /> : r.article}
                      <span className="muted">
                        {place(r.location)} · Soll {fl(r.article, r.expected)}, gezählt {fl(r.article, r.counted)}
                      </span>
                    </span>
                    <span className={`side ${r.diff < 0 ? "negative" : ""}`}>
                      <span>{signedFl(r.article, r.diff)}</span>
                      {prices && <span>{euro(r.value)}</span>}
                    </span>
                  </div>
                  {r.causes.map((c) => (
                    <div key={c.booking} className="row between cause">
                      <span className="muted">
                        davon {reasonLabel(c.reason)}
                        {!closed && (
                          <>
                            {" · "}
                            <button className="link inline" onClick={() => undo(c.booking)}>
                              zurücknehmen
                            </button>
                          </>
                        )}
                      </span>
                      <span className="side">
                        <span>{fmtDiff(catalog.articles.get(r.article), -c.qty)}</span>
                        {prices && <span className="muted">{euro(-c.value)}</span>}
                      </span>
                    </div>
                  ))}
                  {r.causes.length > 0 && (
                    <div className="row between cause">
                      <strong>{r.rest < 0 ? "Schwund" : r.rest > 0 ? "mehr als gebucht" : "Rest"}</strong>
                      <strong className="side">
                        <span>{signedFl(r.article, r.rest)}</span>
                        {prices && <span>{euro(r.restValue)}</span>}
                      </strong>
                    </div>
                  )}
                  {!closed && r.rest < 0 && assigning !== key && (
                    <button className="link" onClick={() => setAssigning(key)} data-testid={`assign-${r.article}`}>
                      Ursache zuordnen …
                    </button>
                  )}
                  {!closed && assigning === key && <Assign row={r} inventory={inventory} onDone={() => setAssigning(null)} />}
                </li>
              );
            })}
        </ul>
        {shownReport.uncounted.length > 0 && (
          <details>
            <summary>
              {shownReport.uncounted.length} {shownReport.uncounted.length === 1 ? (drinks ? "Getränk" : "Wein") : drinks ? "Getränke" : "Weine"} nicht gezählt
            </summary>
            <ul className="list">
              {shownReport.uncounted.map((u) => (
                <li key={`${u.article}|${u.location}`} className="row between">
                  <span>
                    {name(u.article)}
                    <br />
                    <span className="muted">{place(u.location)}</span>
                  </span>
                  <span className="side muted">
                    <Parts a={catalog.articles.get(u.article)} qty={u.qty} />
                  </span>
                </li>
              ))}
            </ul>
            <p className="hint">Nicht gezählt heißt nicht 0: Diese {drinks ? "Getränke" : "Weine"} behalten ihren Bestand laut Journal.</p>
          </details>
        )}
      </div>
      <div className={wide ? "pane side-pane" : ""}>
        <div className="card">
          <h3>Summe</h3>
          <table className="grid">
            <tbody>
              <tr>
                <td>Differenz</td>
                <td className="num stack-cell">
                  <Lines parts={diffBottles.parts(true)} />
                </td>
                {prices && <td className="num">{euro(t.value)}</td>}
              </tr>
              <tr>
                <td>davon zugeordnet</td>
                <td className="num stack-cell">
                  <Lines parts={causeBottles.parts()} />
                </td>
                {prices && <td className="num">{euro(-t.causesValue)}</td>}
              </tr>
              <tr className="negative">
                <td>
                  <strong>Schwund</strong>
                </td>
                <td className="num stack-cell">
                  <strong>
                    <Lines parts={short.qty.parts()} />
                  </strong>
                </td>
                {prices && (
                  <td className="num">
                    <strong>{euro(short.value)}</strong>
                  </td>
                )}
              </tr>
              {more.qty.total > 0 && (
                <tr>
                  <td>mehr als gebucht</td>
                  <td className="num stack-cell">
                    <Lines parts={more.qty.parts(true)} />
                  </td>
                  {prices && <td className="num">{euro(more.value)}</td>}
                </tr>
              )}
            </tbody>
          </table>
          <p className="muted">
            {shownReport.rows.length} gezählt, {shownReport.uncounted.length} nicht gezählt.
          </p>
          <div className="buttons">
            <button onClick={exportCsv}>Als Excel-Tabelle (CSV)</button>
          </div>
          {!closed &&
            (closing ? (
              <div className="banner-box">
                <p>Abschließen friert den Bericht ein. Danach wird nicht mehr zugeordnet; verspätete Buchungen erscheinen als Hinweis.</p>
                <div className="buttons">
                  <button className="primary" onClick={close} data-testid="inventory-close-confirm">
                    Jetzt abschließen
                  </button>
                  <button className="link" onClick={() => setClosing(false)}>
                    Noch nicht
                  </button>
                </div>
              </div>
            ) : (
              <button className="primary big" onClick={() => setClosing(true)} data-testid="inventory-close">
                Inventur abschließen
              </button>
            ))}
        </div>
      </div>
    </div>
  );

  async function undo(booking: string) {
    const b = store.replica?.bookings.get(booking) ?? store.replica?.journal().find((x) => x.id === booking);
    if (!b || b.kind !== "out") return;
    await store.book({ kind: "cancel", cancels: b.id, lagers: [b.lager] } as Booking);
  }
}

/** Books a known cause: a real issue right before the first count, marked for this inventory. */
function Assign({ row, inventory, onDone }: { row: InventoryRow; inventory: Inventory; onDone: () => void }) {
  const { store } = useStore();
  const kind = store.config?.kind === "private" ? "private" : "business";
  const reasons = wineReasons("out", kind).sort((a, b) => Number(b.class === "knownLoss") - Number(a.class === "knownLoss"));
  const [reason, setReason] = useState(reasons[0]?.id ?? "breakage");
  // Entered in bottles (kegs, cans), booked in ml.
  const article = useCatalog()?.articles.get(row.article);
  const bottle = article?.bottle ?? 1;
  const [qty, setQty] = useState(String(Math.floor(-row.rest / bottle)));

  async function book() {
    const n = Math.floor(Number(qty));
    if (!(n > 0)) return;
    await store.book({ kind: "out", article: row.article, lager: row.lager, location: row.location, qty: n * bottle, reason, note: causeNote(inventory.id), at: row.firstAt - 1 } as Booking & { at: number });
    onDone();
  }

  return (
    <div className="assign row">
      <select value={reason} onChange={(e) => setReason(e.target.value)} aria-label="Ursache" data-testid="assign-reason">
        {reasons.map((r) => (
          <option key={r.id} value={r.id}>
            {r.label}
          </option>
        ))}
      </select>
      <input className="narrow" inputMode="numeric" value={qty} onChange={(e) => setQty(e.target.value)} aria-label={article ? containerWords(article).many : "Flaschen"} data-testid="assign-qty" />
      <button className="primary" onClick={book} data-testid="assign-book">
        Zuordnen
      </button>
      <button className="link" onClick={onDone}>
        abbrechen
      </button>
    </div>
  );
}
